import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { visibleInterval } from '@/lib/polling';
import { printDocument } from '@/lib/print';
import { DataTable, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Alert, Button, DatePicker, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ApprovalModal } from './ApprovalModal';
import { usePrintReceipt } from './receipt-print';

interface ShiftView {
  id: string;
  counterName: string;
  cashier: string | null;
  openedAt: string;
  closedAt: string | null;
}

interface Person {
  id: string;
  name: string | null;
}

interface RefundRow {
  id: string;
  status: string;
  amountMinor: number;
  reason: string;
  notes: string | null;
  receiptNo: string | null;
  requestedAt: string;
  paidAt: string | null;
  requestedBy: Person | null;
  decidedBy: Person | null;
  payment: {
    id: string;
    mode: string;
    amountMinor: number;
    txnRef: string | null;
    receiptNo: string | null;
    receivedAt: string;
    refundedMinor: number;
    shift: ShiftView | null;
  };
  invoice: { id: string; number: string; status: string; walkInName: string | null; patient: { id: string; mrn: string; fullName: string } | null } | null;
  payoutShift: ShiftView | null;
  blockedReasons: string[];
  canDecide: boolean;
}

interface Viewer {
  canApprove: boolean;
  approvalLimitMinor: number;
  unlimited: boolean;
  hasOpenShift: boolean;
  cashNeedsShift: boolean;
}

interface RefundPage {
  data: RefundRow[];
  nextCursor: string | null;
  viewer: Viewer;
}

interface CreditNoteRow {
  id: string;
  number: string;
  status: string;
  kind: string;
  reason: string;
  settlement: string;
  netMinor: number;
  taxMinor: number;
  requestedAt: string;
  requestedBy: Person | null;
  decidedBy: Person | null;
  decisionNotes: string | null;
  invoice: { id: string; number: string; walkInName: string | null; patient: { id: string; mrn: string; fullName: string } | null };
  blockedReasons: string[];
  canDecide: boolean;
}

interface CreditNotePage {
  data: CreditNoteRow[];
  nextCursor: string | null;
  viewer: { canApprove: boolean; approvalLimitMinor: number; unlimited: boolean };
}

const SETTLEMENT_TEXT: Record<string, string> = {
  none: 'Reduces the balance owing',
  refund: 'Refund the customer',
  keep_credit: 'Keep as credit on the invoice',
};

function shiftText(shift: ShiftView | null): string {
  if (!shift) return 'no shift';
  return `${shift.counterName}${shift.cashier ? ` (${shift.cashier})` : ''}, ${shift.closedAt ? 'closed' : 'open'}`;
}

function limitBanner(viewer: { canApprove: boolean; approvalLimitMinor: number; unlimited: boolean }): string {
  if (viewer.unlimited) return 'You can approve refunds of any size.';
  if (viewer.canApprove) return `Your approval limit is ${money(viewer.approvalLimitMinor)}. Anything above it waits for a supervisor with a higher limit.`;
  return 'You can request refunds but not approve them. A supervisor decides each request.';
}

export function RefundsPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState('refunds');
  const showCreditNotes = can('invoice.credit_note', 'payment.refund.approve', 'invoice.read');

  return (
    <>
      <PageHeader title={tr('billing', 'refunds_and_credit_notes')} description={tr('billing', 'money_handed_back_to_patients_and_reductions_of')} />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[{ id: 'refunds', label: tr('billing', 'refunds') }, ...(showCreditNotes ? [{ id: 'credit', label: tr('billing', 'credit_notes') }] : [])]}
      />
      {tab === 'refunds' ? <RefundsTab /> : <CreditNotesTab />}
    </>
  );
}

function RefundsTab() {
  const { can } = useAuth();
  const defaultStatus = can('payment.refund.approve') ? 'requested' : '';
  const [status, setStatus] = useState(defaultStatus);
  const [mode, setMode] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [deciding, setDeciding] = useState<RefundRow | null>(null);
  const print = usePrintReceipt();

  const query = useInfiniteQuery({
    queryKey: ['refunds', status, mode, from, to],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<RefundPage>(
        `/refunds${qs({ status, mode, from, to, limit: 50, cursor: pageParam })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: status === 'requested' ? visibleInterval(60_000) : false,
  });
  const rows = query.data?.pages.flatMap((page) => page.data);
  const viewer = query.data?.pages[0]?.viewer;

  return (
    <>
      {viewer && (
        <Alert className="mb-3" variant={viewer.canApprove ? 'default' : 'warning'}>
          {limitBanner(viewer)}
          {viewer.cashNeedsShift && !viewer.hasOpenShift && ' You have no open cashier shift, so cash refunds cannot be paid out by you.'}
        </Alert>
      )}

      <SearchFilterBar
        filters={
          <>
            <FilterField label={tr('billing', 'status')}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44">
                <option value="">{tr('billing', 'all_statuses')}</option>
                <option value="requested">{tr('billing', 'waiting_for_approval')}</option>
                <option value="paid">{tr('billing', 'paid_out')}</option>
                <option value="rejected">{tr('billing', 'rejected')}</option>
              </Select>
            </FilterField>
            <FilterField label={tr('billing', 'payment_mode')}>
              <Select value={mode} onChange={(e) => setMode(e.target.value)} className="w-44">
                <option value="">{tr('billing', 'every_mode')}</option>
                {['cash', 'card', 'bank_transfer', 'mobile_wallet', 'online_gateway'].map((m) => (
                  <option key={m} value={m}>
                    {humanize(m)}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label={tr('billing', 'from_date')}>
              <DatePicker value={from} onChange={setFrom} className="w-40" />
            </FilterField>
            <FilterField label={tr('billing', 'to_date')}>
              <DatePicker value={to} onChange={setTo} className="w-40" />
            </FilterField>
          </>
        }
        onReset={
          status !== defaultStatus || mode || from || to
            ? () => {
                setStatus(defaultStatus);
                setMode('');
                setFrom('');
                setTo('');
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={query.isLoading} refreshing={query.isFetching}
        error={query.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_refunds_match')}
        footer={
          query.hasNextPage ? (
            <Button variant="outline" size="sm" className="mx-auto" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
              {tr('billing', 'show_more')}
            </Button>
          ) : undefined
        }
        columns={[
          {
            header: tr('billing', 'requested'),
            sort: (row) => row.requestedAt,
            cell: (row) => (
              <div>
                <p>{dateTime(row.requestedAt)}</p>
                <p className="text-xs text-muted-foreground">{row.requestedBy?.name ?? '—'}</p>
              </div>
            ),
          },
          {
            header: tr('billing', 'bill'),
            sort: (row) => row.invoice?.number,
            cell: (row) =>
              row.invoice ? (
                <div>
                  <Link className="underline" to={`/billing/invoices/${row.invoice.id}`}>
                    {row.invoice.number}
                  </Link>
                  <p className="text-xs text-muted-foreground">{row.invoice.patient?.fullName ?? row.invoice.walkInName ?? tr('billing', 'walk_in')}</p>
                </div>
              ) : (
                '—'
              ),
          },
          {
            header: tr('billing', 'original_payment'),
            cell: (row) => (
              <div className="text-xs">
                <p>
                  {humanize(row.payment.mode)} · {money(row.payment.amountMinor)}
                </p>
                <p className="text-muted-foreground">
                  {row.payment.receiptNo ?? tr('billing', 'no_receipt_no')}
                  {row.payment.txnRef ? ` · ref ${row.payment.txnRef}` : ''}
                </p>
                <p className="text-muted-foreground">{tr('billing', 'taken_in')} {shiftText(row.payment.shift)}</p>
              </div>
            ),
          },
          { header: tr('billing', 'refund'), align: 'right', sort: (row) => row.amountMinor, cell: (row) => <strong>{money(row.amountMinor)}</strong> },
          {
            header: tr('billing', 'reason'),
            sort: (row) => row.reason,
            cell: (row) => (
              <div className="max-w-xs text-xs">
                <p>{row.reason}</p>
                {row.notes && <p className="text-muted-foreground">{tr('billing', 'decision_note')} {row.notes}</p>}
              </div>
            ),
          },
          {
            header: tr('billing', 'status'),
            sort: (row) => row.status,
            cell: (row) => (
              <div className="text-xs">
                <StatusBadge status={row.status === 'requested' ? 'requested' : row.status} />
                {row.decidedBy && <p className="mt-1 text-muted-foreground">{tr('billing', 'by')} {row.decidedBy.name ?? tr('billing', 'staff')}</p>}
                {row.status === 'paid' && (
                  <p className="text-muted-foreground">
                    {tr('billing', 'paid_out')} {row.paidAt ? dateTime(row.paidAt) : ''} · {shiftText(row.payoutShift)}
                  </p>
                )}
                {row.status === 'requested' && row.blockedReasons.length > 0 && (
                  <p className="mt-1 text-warning">{tr('billing', 'blocked')} {row.blockedReasons[0]}</p>
                )}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {row.status === 'requested' && (
                  <Button size="sm" variant="outline" onClick={() => setDeciding(row)}>
                    {tr('billing', 'review')}
                  </Button>
                )}
                {row.status === 'paid' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={tr('billing', 'print_voucher')}
                    onClick={() => print.mutate({ kind: 'refund', id: row.id, size: 'thermal' })}
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {deciding && viewer && <RefundDecision refund={deciding} viewer={viewer} onClose={() => setDeciding(null)} />}
    </>
  );
}

function RefundDecision({ refund, viewer, onClose }: { refund: RefundRow; viewer: Viewer; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (input: { decision: 'approved' | 'rejected'; notes: string }) =>
      post(`/refunds/${refund.id}/decision`, input, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: (_result, input) => {
      toast.success(input.decision === 'approved' ? tr('billing', 'refund_approved_and_paid_out') : tr('billing', 'refund_rejected'));
      void queryClient.invalidateQueries({ queryKey: ['refunds'] });
      void queryClient.invalidateQueries({ queryKey: ['invoice'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <ApprovalModal
      title={tr('billing', 'refund_of_x', { amountMinor: money(refund.amountMinor) })}
      description={refund.invoice ? tr('billing', 'bill_x', { number: refund.invoice.number }) : undefined}
      facts={[
        { label: tr('billing', 'asked_for_by'), value: refund.requestedBy?.name ?? '—' },
        { label: tr('billing', 'reason_given'), value: refund.reason },
        { label: tr('billing', 'original_payment'), value: `${humanize(refund.payment.mode)} · ${money(refund.payment.amountMinor)}` },
        { label: tr('billing', 'already_refunded_from_it'), value: money(refund.payment.refundedMinor) },
        { label: tr('billing', 'payment_was_taken_in'), value: shiftText(refund.payment.shift) },
        { label: tr('billing', 'your_approval_limit'), value: viewer.unlimited ? 'No limit' : viewer.canApprove ? money(viewer.approvalLimitMinor) : 'You cannot approve' },
        { label: tr('billing', 'paid_out_from'), value: refund.payment.mode === 'cash' ? (refund.payment.shift && !refund.payment.shift.closedAt ? shiftText(refund.payment.shift) : viewer.hasOpenShift ? 'Your open shift' : 'No open shift') : 'Not a cash refund' },
      ]}
      blockedReasons={refund.blockedReasons}
      canDecide={refund.canDecide}
      canApprove={viewer.canApprove}
      busy={decide.isPending}
      error={error}
      onApprove={(notes) => {
        setError(null);
        decide.mutate({ decision: 'approved', notes });
      }}
      onReject={(notes) => {
        setError(null);
        decide.mutate({ decision: 'rejected', notes });
      }}
      onClose={onClose}
    />
  );
}

function CreditNotesTab() {
  const [status, setStatus] = useState('requested');
  const [deciding, setDeciding] = useState<CreditNoteRow | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['credit-notes', status],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<CreditNotePage>(`/credit-notes${qs({ status, limit: 50, cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: status === 'requested' ? visibleInterval(60_000) : false,
  });
  const rows = query.data?.pages.flatMap((page) => page.data);
  const viewer = query.data?.pages[0]?.viewer;
  const toast = useToast();

  const print = useMutation({
    mutationFn: (id: string) => get<{ html: string }>(`/credit-notes/${id}/print`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('billing', 'could_not_print'), describeError(error)),
  });

  return (
    <>
      {viewer && <Alert className="mb-3" variant={viewer.canApprove ? 'default' : 'warning'}>{limitBanner(viewer)}</Alert>}
      <SearchFilterBar
        filters={
          <FilterField label={tr('billing', 'status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-48">
              <option value="">{tr('billing', 'all_statuses')}</option>
              <option value="requested">{tr('billing', 'waiting_for_approval')}</option>
              <option value="applied">{tr('billing', 'applied')}</option>
              <option value="rejected">{tr('billing', 'rejected')}</option>
            </Select>
          </FilterField>
        }
        onReset={status !== 'requested' ? () => setStatus('requested') : undefined}
      />

      <DataTable
        rows={rows}
        loading={query.isLoading} refreshing={query.isFetching}
        error={query.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_credit_notes_match')}
        footer={
          query.hasNextPage ? (
            <Button variant="outline" size="sm" className="mx-auto" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
              {tr('billing', 'show_more')}
            </Button>
          ) : undefined
        }
        columns={[
          { header: tr('billing', 'credit_note'), sort: (row) => row.number, cell: (row) => <span className="tabular font-medium">{row.number}</span> },
          {
            header: tr('billing', 'bill'),
            sort: (row) => row.invoice.number,
            cell: (row) => (
              <div>
                <Link className="underline" to={`/billing/invoices/${row.invoice.id}`}>
                  {row.invoice.number}
                </Link>
                <p className="text-xs text-muted-foreground">{row.invoice.patient?.fullName ?? row.invoice.walkInName ?? tr('billing', 'walk_in')}</p>
              </div>
            ),
          },
          { header: tr('billing', 'credit'), align: 'right', sort: (row) => row.netMinor, cell: (row) => <strong>{money(row.netMinor)}</strong> },
          { header: tr('billing', 'then'), sort: (row) => row.settlement, cell: (row) => SETTLEMENT_TEXT[row.settlement] ?? row.settlement },
          { header: tr('billing', 'reason'), sort: (row) => row.reason, cell: (row) => <span className="text-xs">{row.reason}</span> },
          {
            header: tr('billing', 'requested'),
            sort: (row) => row.requestedAt,
            cell: (row) => (
              <div className="text-xs">
                <p>{dateTime(row.requestedAt)}</p>
                <p className="text-muted-foreground">{row.requestedBy?.name ?? '—'}</p>
              </div>
            ),
          },
          {
            header: tr('billing', 'status'),
            sort: (row) => row.status,
            cell: (row) => (
              <div className="text-xs">
                <StatusBadge status={row.status} />
                {row.decidedBy && <p className="mt-1 text-muted-foreground">{tr('billing', 'by')} {row.decidedBy.name ?? tr('billing', 'staff')}</p>}
                {row.status === 'requested' && row.blockedReasons.length > 0 && (
                  <p className="mt-1 text-warning">{tr('billing', 'blocked')} {row.blockedReasons[0]}</p>
                )}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {row.status === 'requested' && (
                  <Button size="sm" variant="outline" onClick={() => setDeciding(row)}>
                    {tr('billing', 'review')}
                  </Button>
                )}
                <Button size="sm" variant="ghost" aria-label={tr('billing', 'print_credit_note')} onClick={() => print.mutate(row.id)}>
                  <Printer className="h-4 w-4" />
                </Button>
              </div>
            ),
          },
        ]}
      />

      {deciding && viewer && <CreditNoteDecision note={deciding} viewer={viewer} onClose={() => setDeciding(null)} />}
    </>
  );
}

function CreditNoteDecision({
  note,
  viewer,
  onClose,
}: {
  note: CreditNoteRow;
  viewer: { canApprove: boolean; approvalLimitMinor: number; unlimited: boolean };
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (input: { decision: 'approved' | 'rejected'; notes: string }) =>
      post(`/credit-notes/${note.id}/decision`, input, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: (_result, input) => {
      toast.success(input.decision === 'approved' ? tr('billing', 'credit_note_approved') : tr('billing', 'credit_note_rejected'));
      void queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      void queryClient.invalidateQueries({ queryKey: ['invoice'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <ApprovalModal
      title={tr('billing', 'credit_note_x', { number: note.number })}
      description={tr('billing', 'against_bill_x', { number: note.invoice.number })}
      facts={[
        { label: tr('billing', 'credit'), value: money(note.netMinor) },
        { label: tr('billing', 'vat_in_it'), value: money(note.taxMinor) },
        { label: tr('billing', 'asked_for_by'), value: note.requestedBy?.name ?? '—' },
        { label: tr('billing', 'reason_given'), value: note.reason },
        { label: tr('billing', 'what_happens_next'), value: SETTLEMENT_TEXT[note.settlement] ?? note.settlement },
        { label: tr('billing', 'your_approval_limit'), value: viewer.unlimited ? 'No limit' : viewer.canApprove ? money(viewer.approvalLimitMinor) : 'You cannot approve' },
      ]}
      blockedReasons={note.blockedReasons}
      canDecide={note.canDecide}
      canApprove={viewer.canApprove}
      busy={decide.isPending}
      error={error}
      onApprove={(notes) => {
        setError(null);
        decide.mutate({ decision: 'approved', notes });
      }}
      onReject={(notes) => {
        setError(null);
        decide.mutate({ decision: 'rejected', notes });
      }}
      onClose={onClose}
    />
  );
}
